import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormHelperText,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  Alert,
  CircularProgress,
  InputAdornment,
  alpha,
  useTheme
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  LibraryBooks as LibraryBooksIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import * as financeService from '../../../services/financeService';

// Initial account options while loading real accounts
const initialAccountOptions = [
  { id: '1000', name: 'الأصول', accountNumber: '1000' },
  { id: '1100', name: 'الأصول المتداولة', accountNumber: '1100' },
  { id: '1110', name: 'النقدية', accountNumber: '1110' },
  { id: '1200', name: 'معدات مكتبية', accountNumber: '1200' },
  { id: '2000', name: 'الخصوم', accountNumber: '2000' },
  { id: '3000', name: 'الإيرادات', accountNumber: '3000' },
  { id: '3100', name: 'إيرادات المبيعات', accountNumber: '3100' },
  { id: '4000', name: 'المصروفات', accountNumber: '4000' },
  { id: '4100', name: 'مصاريف الرواتب', accountNumber: '4100' }
];

// Internal component types (extended from service types)
interface JournalEntryLine {
  id: string;
  accountId: string;
  description: string;
  debit: number | '';
  credit: number | '';
  lineNumber: number; // تحديث: جعل الحقل إلزامي بدلاً من اختياري
  [key: string]: any; // إضافة فهرس ديناميكي للسماح بالوصول إلى الحقول بشكل ديناميكي
}

interface JournalEntryData {
  id?: string;
  entryNumber: string;
  date: string;
  description: string;
  status: string;
  lines: JournalEntryLine[];
}

interface AccountOption {
  id: string;
  accountNumber: string;
  name: string;
}

// قيد محاسبي فارغ للإنشاء
const emptyEntry: JournalEntryData = {
  entryNumber: `JE-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 999) + 1).padStart(3, '0')}`,
  date: new Date().toISOString().split('T')[0],
  description: '',
  status: 'مسودة',
  lines: [
    { id: 'new-1', accountId: '', description: '', debit: '', credit: '', lineNumber: 1 },
    { id: 'new-2', accountId: '', description: '', debit: '', credit: '', lineNumber: 2 }
  ]
};

const JournalEntryForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isEditMode = Boolean(id);
  
  // حالة النموذج
  const [formData, setFormData] = useState<JournalEntryData>(emptyEntry);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lineErrors, setLineErrors] = useState<Record<string, Record<string, string>>>({});
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [accountOptions, setAccountOptions] = useState<AccountOption[]>(initialAccountOptions);
  
  // Load accounts from API
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const accounts = await financeService.getAccounts();
        if (accounts && accounts.length > 0) {
          setAccountOptions(accounts.map(account => ({
            id: account.id,
            accountNumber: account.accountNumber,
            name: account.name
          })));
        }
      } catch (error) {
        console.error('Error loading accounts:', error);
        // Continue with default accounts if API call fails
      }
    };
    
    fetchAccounts();
  }, []);
  
  // Load journal entry data when in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      setLoading(true);
      setApiError(null);
      
      const fetchJournalEntry = async () => {
        try {
          const entry = await financeService.getJournalEntryById(id);
          
          // Transform API response to form data
          const formattedEntry: JournalEntryData = {
            id: entry.id,
            entryNumber: entry.entryNumber || `JE-${new Date().getFullYear()}-00000`,
            date: entry.date.split('T')[0], // Format date for input
            description: entry.description,
            status: entry.status || 'مسودة',
            lines: entry.lines.map((line: any, index) => {
              // استخدام any هنا لأن البيانات تأتي من الخادم ولا نعرف شكلها بالضبط
              const lineNumber = line.lineNumber ? Number(line.lineNumber) : index + 1;
              
              return {
                id: line.id || `edit-line-${Date.now()}-${index}`, // إنشاء معرف فريد لكل سطر
                accountId: line.accountId,
                description: line.description || '',
                debit: line.debit,
                credit: line.credit,
                lineNumber: lineNumber // ضمان أن القيمة رقمية
              };
            })
          };
          
          setFormData(formattedEntry);
          setLoading(false);
        } catch (error) {
          console.error('Error fetching journal entry:', error);
          setApiError('حدث خطأ أثناء تحميل بيانات القيد المحاسبي');
          setLoading(false);
        }
      };
      
      fetchJournalEntry();
    }
  }, [id, isEditMode]);

  // معالجة تغيير الحقول العامة
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // مسح رسالة الخطأ عند التغيير
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  // معالجة تغيير حقول Select
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    
    if (name) {
      setFormData(prev => ({ ...prev, [name]: value }));
      
      // مسح رسالة الخطأ عند التغيير
      if (errors[name]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  // معالجة تغيير سطر القيد - تحسين جذري لمنع تكرار التعديلات
  const handleLineChange = (index: number, field: string, value: any, additionalUpdate?: { field: string, value: any }) => {
    console.log(`[handleLineChange] بداية تحديث السطر بالفهرس ${index}, الحقل ${field}, القيمة:`, value);
    
    // تعميق التتبع لتشخيص المشكلة
    console.log(`بيانات قبل التحديث:`, JSON.stringify(formData.lines.map(l => ({ id: l.id, field, value: (l as any)[field] }))));
    
    setFormData(prevFormData => {
      // إنشاء نسخة عميقة بالكامل من البيانات - هذا مهم جداً لمنع تداخل المراجع
      const newFormData = JSON.parse(JSON.stringify(prevFormData));
      
      // التأكد من وجود السطر بالفهرس المحدد
      if (index >= 0 && index < newFormData.lines.length) {
        const lineId = newFormData.lines[index].id;
        console.log(`تحديث السطر رقم ${index} بمعرف ${lineId}`);
        
        // تحديث الحقل المطلوب فقط في السطر المحدد
        newFormData.lines[index][field] = value;
        
        // تطبيق التحديث الإضافي إذا كان موجودًا
        if (additionalUpdate) {
          newFormData.lines[index][additionalUpdate.field] = additionalUpdate.value;
        }
        
        // تحديث أرقام الأسطر للتأكد من صحتها
        newFormData.lines.forEach((line: any, i: number) => {
          line.lineNumber = i + 1;
        });
        
        console.log(`تم تحديث السطر رقم ${index} بنجاح`);
        console.log('السطر بعد التحديث:',
          `معرف=${newFormData.lines[index].id}, حقل=${field}, قيمة=${newFormData.lines[index][field]}`
        );
      } else {
        console.error(`الفهرس ${index} خارج نطاق مصفوفة الأسطر (الطول: ${newFormData.lines.length})`);
      }
      
      return newFormData;
    });
    
    // تحديث رسائل الخطأ
    const lineId = formData.lines[index]?.id;
    if (lineId && lineErrors[lineId]?.[field]) {
      setLineErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors[lineId]) {
          delete newErrors[lineId][field as string];
          if (Object.keys(newErrors[lineId]).length === 0) {
            delete newErrors[lineId];
          }
        }
        return newErrors;
      });
    }
    
    // مسح الخطأ للحقل الإضافي أيضًا إذا كان موجودًا
    if (lineId && additionalUpdate && lineErrors[lineId]?.[additionalUpdate.field]) {
      setLineErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors[lineId]) {
          delete newErrors[lineId][additionalUpdate.field as string];
          if (Object.keys(newErrors[lineId]).length === 0) {
            delete newErrors[lineId];
          }
        }
        return newErrors;
      });
    }
  };

  // إضافة سطر جديد - تحسين إضافي للتأكد من فصل كامل للمراجع
  const addLine = () => {
    // إنشاء معرف فريد للسطر الجديد باستخدام طابع زمني ورقم عشوائي
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    const newId = `line-${timestamp}-${random}`;
    
    // حساب رقم السطر الجديد
    const newLineNumber = formData.lines.length + 1;
    
    console.log(`[addLine] إضافة سطر جديد بمعرف ${newId} ورقم ${newLineNumber}`);
    
    // استخدام الدالة الوظيفية مع نسخة عميقة
    setFormData(prevFormData => {
      // إنشاء نسخة عميقة بالكامل للتأكد من عدم وجود مراجع مشتركة
      const newFormData = JSON.parse(JSON.stringify(prevFormData));
      
      // إضافة السطر الجديد مع كل البيانات اللازمة
      newFormData.lines.push({
        id: newId,
        accountId: '',
        description: '',
        debit: '',
        credit: '',
        lineNumber: newLineNumber, // إضافة رقم السطر كحقل مطلوب
        rowKey: `new-row-${timestamp}` // إضافة مفتاح صف فريد
      });
      
      // تطبيق التغييرات
      return newFormData;
    });
  };

  // حذف سطر - تم التحسين لاستخدام الفهرس مباشرة
  const removeLine = (lineId: string, index?: number) => {
    // التحقق من الحد الأدنى للسطور
    if (formData.lines.length <= 2) {
      alert('يجب أن يحتوي القيد على سطرين على الأقل');
      return;
    }
    
    console.log(`[removeLine] جاري حذف السطر بالمعرف ${lineId}`);
    
    // استخدام طريقة وظيفية لتحديث البيانات
    setFormData(prevFormData => {
      // نسخ البيانات بأكملها
      const newFormData = { ...prevFormData };
      
      // نسخ مصفوفة الأسطر
      const newLines = [...prevFormData.lines];
      
      // حذف السطر باستخدام الفهرس إذا كان متاحًا
      if (typeof index === 'number' && index >= 0 && index < newLines.length) {
        console.log(`حذف السطر بالفهرس ${index}`);
        newLines.splice(index, 1);
      } else {
        // حذف السطر باستخدام المعرف
        console.log(`حذف السطر بالمعرف ${lineId} بدون فهرس`);
        newFormData.lines = newLines.filter(line => line.id !== lineId);
        return newFormData;
      }
      
      // تحديث الأسطر في البيانات
      newFormData.lines = newLines;
      
      return newFormData;
    });
    
    // مسح أخطاء السطر عند حذفه
    if (lineErrors[lineId]) {
      setLineErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[lineId];
        return newErrors;
      });
    }
  };

  // حساب المجاميع
  const getTotals = () => {
    const totals = formData.lines.reduce(
      (acc, line) => {
        acc.debit += typeof line.debit === 'number' ? line.debit : 0;
        acc.credit += typeof line.credit === 'number' ? line.credit : 0;
        return acc;
      },
      { debit: 0, credit: 0 }
    );
    
    return totals;
  };

  // التحقق من توازن القيد
  const isBalanced = () => {
    const totals = getTotals();
    return totals.debit === totals.credit;
  };

  // التحقق من صحة البيانات
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    const newLineErrors: Record<string, Record<string, string>> = {};
    
    if (!formData.date) {
      newErrors.date = 'التاريخ مطلوب';
    }
    
    // البيان أصبح اختيارياً - تم إزالة التحقق من الإلزامية
    
    formData.lines.forEach(line => {
      const lineError: Record<string, string> = {};
      
      if (!line.accountId) {
        lineError.accountId = 'الحساب مطلوب';
      }
      
      if ((line.debit === '' || line.debit === 0) && (line.credit === '' || line.credit === 0)) {
        lineError.debit = 'يجب إدخال قيمة مدين أو دائن';
        lineError.credit = 'يجب إدخال قيمة مدين أو دائن';
      }
      
      if (line.debit !== '' && line.credit !== '' && line.debit !== 0 && line.credit !== 0) {
        lineError.debit = 'لا يمكن إدخال قيمة مدين ودائن معًا لنفس السطر';
        lineError.credit = 'لا يمكن إدخال قيمة مدين ودائن معًا لنفس السطر';
      }
      
      if (Object.keys(lineError).length > 0) {
        newLineErrors[line.id] = lineError;
      }
    });
    
    if (!isBalanced()) {
      newErrors.balance = 'القيد غير متوازن (مجموع المدين لا يساوي مجموع الدائن)';
    }
    
    setErrors(newErrors);
    setLineErrors(newLineErrors);
    
    return Object.keys(newErrors).length === 0 && Object.keys(newLineErrors).length === 0;
  };

  // معالجة حفظ النموذج
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSaveLoading(true);
    setApiError(null);
    try {
      // إضافة معلومات تشخيصية إضافية عند التعديل
      console.log(`[handleSubmit] نوع العملية: ${isEditMode ? 'تعديل' : 'إنشاء جديد'}`);
      console.log(`[handleSubmit] الأسطر المرسلة: ${formData.lines.length}`);
      
      // التأكد من تحديث أرقام الأسطر قبل الإرسال
      const formDataWithLineNumbers = {
        ...formData,
        lines: formData.lines.map((line, index) => ({
          ...line,
          lineNumber: index + 1, // تحديث أرقام الأسطر لتكون متسلسلة
          rowIndex: index // إضافة مؤشر صف إضافي للتتبع
        }))
      };
      
      // إضافة معلومات تقنية إضافية لمساعدة الخادم في معالجة التعديلات
      // استخدام تحويل النوع لتجنب أخطاء TypeScript
      const enhancedFormData = {
        ...formDataWithLineNumbers,
        updateType: 'manual',
        clientTimestamp: Date.now()
      };
      
      // تحويل البيانات إلى الصيغة المطلوبة للواجهة البرمجية
      const entryData = financeService.formatJournalEntryForApi(enhancedFormData);
      
      console.log('بيانات القيد المحاسبي المرسلة:', JSON.stringify(entryData));
      
      let response;
      if (isEditMode && formData.id) {
        // إضافة معلومات التتبع إلى البيانات المرسلة بدلاً من رؤوس الطلب
        const entryDataWithTracking = {
          ...entryData,
          _updateType: 'full-update',
          _clientTimestamp: Date.now()
        };
        
        // تحديث قيد موجود
        response = await financeService.updateJournalEntry(formData.id, entryDataWithTracking);
      } else {
        // إنشاء قيد جديد
        response = await financeService.createJournalEntry(entryData);
      }
      
      setSaveLoading(false);
      setSuccess(true);
      
      // العودة إلى صفحة القائمة بعد النجاح
      setTimeout(() => {
        navigate('/finance/ledger');
      }, 1500);
    } catch (error: any) {
      setSaveLoading(false);
      
      if (error.response?.data?.message) {
        setApiError(error.response.data.message);
      } else {
        setApiError('حدث خطأ أثناء حفظ القيد المحاسبي');
      }
      
      console.error('Error saving journal entry:', error);
    }
  };

  // العنوان بناءً على وضع النموذج
  const title = isEditMode ? 'تعديل قيد محاسبي' : 'إضافة قيد محاسبي جديد';
  const totals = getTotals();

  return (
    <Box sx={{ mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid',
          borderColor: alpha('#000', 0.08),
          background: 'white'
        }}
      >
        {/* العنوان ومسار التنقل */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', mb: 1 }}>
              <LibraryBooksIcon sx={{ mr: 1, color: '#30c3b0' }} />
              {title}
            </Typography>
            
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <Link to="/finance/ledger" style={{ textDecoration: 'none', color: theme.palette.primary.main }}>
                دفتر الأستاذ العام
              </Link>
              <Typography color="text.primary">{title}</Typography>
            </Breadcrumbs>
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to="/finance/ledger"
            sx={{
              borderColor: '#30c3b0',
              color: '#30c3b0',
              '&:hover': { borderColor: '#25a899', backgroundColor: alpha('#30c3b0', 0.04) },
            }}
          >
            العودة للقائمة
          </Button>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {/* عرض التحميل أثناء جلب البيانات */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: '#30c3b0' }} />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  name="entryNumber"
                  label="رقم القيد"
                  value={formData.entryNumber}
                  fullWidth
                  disabled
                  margin="normal"
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  name="date"
                  label="تاريخ القيد"
                  type="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  error={!!errors.date}
                  helperText={errors.date}
                  fullWidth
                  margin="normal"
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel id="status-label">حالة القيد</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status-select"
                    name="status"
                    value={formData.status || 'مسودة'}
                    onChange={handleSelectChange}
                    label="حالة القيد"
                  >
                    <MenuItem value="مسودة">مسودة</MenuItem>
                    <MenuItem value="تحت الموافقة">تحت الموافقة</MenuItem>
                    <MenuItem value="مؤكد">مؤكد</MenuItem>
                    <MenuItem value="مرحل">مرحل</MenuItem>
                    <MenuItem value="غير مرحل">غير مرحل</MenuItem>
                    <MenuItem value="مرفوض">مرفوض</MenuItem>
                    <MenuItem value="ملغى">ملغى</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="description"
                  label="البيان (اختياري)"
                  value={formData.description}
                  onChange={handleInputChange}
                  error={!!errors.description}
                  helperText={errors.description}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 4, mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                تفاصيل القيد المحاسبي
              </Typography>
              
              <TableContainer component={Box}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell width="5%">#</TableCell>
                      <TableCell width="30%">الحساب</TableCell>
                      <TableCell width="30%">البيان</TableCell>
                      <TableCell width="15%">مدين (ريال)</TableCell>
                      <TableCell width="15%">دائن (ريال)</TableCell>
                      <TableCell width="5%">حذف</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.lines.map((line, index) => (
                      <TableRow key={`line-${index}-${line.id}`}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <FormControl
                            fullWidth
                            error={!!lineErrors[line.id]?.accountId}
                            size="small"
                          >
                            <Select
                              value={line.accountId}
                              onChange={(e) => handleLineChange(index, 'accountId', e.target.value)}
                              displayEmpty
                            >
                              <MenuItem value="" disabled>اختر الحساب</MenuItem>
                              {accountOptions.map(account => (
                                <MenuItem key={account.id} value={account.id}>
                                  {account.accountNumber} - {account.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {lineErrors[line.id]?.accountId && (
                              <FormHelperText>{lineErrors[line.id]?.accountId}</FormHelperText>
                            )}
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={line.description}
                            onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                            size="small"
                            fullWidth
                            placeholder="وصف السطر (اختياري)"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={line.debit}
                            onChange={(e) => {
                              // تحديث قيمة المدين لهذا السطر فقط مع التأكد من تحويلها لرقم
                              const newValue = e.target.value === '' ? '' : Number(e.target.value);
                              console.log(`تغيير المدين للسطر رقم ${index} إلى القيمة ${newValue}`);
                              
                              // إذا كان المستخدم أضاف قيمة مدين، نجعل قيمة الدائن فارغة للسطر نفسه
                              if (newValue !== '' && newValue !== 0) {
                                // استخدام معلمة additionalUpdate لتحديث الدائن إلى فارغ في نفس العملية
                                handleLineChange(index, 'debit', newValue, { field: 'credit', value: '' });
                              } else {
                                // إذا كانت القيمة فارغة، فقط تحديث المدين
                                handleLineChange(index, 'debit', newValue);
                              }
                            }}
                            size="small"
                            fullWidth
                            error={!!lineErrors[line.id]?.debit}
                            helperText={lineErrors[line.id]?.debit}
                            InputProps={{
                              inputProps: { min: 0 },
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Typography variant="caption" color="text.secondary">
                                    ريال
                                  </Typography>
                                </InputAdornment>
                              )
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={line.credit}
                            onChange={(e) => {
                              // تحديث قيمة الدائن لهذا السطر فقط مع التأكد من تحويلها لرقم
                              const newValue = e.target.value === '' ? '' : Number(e.target.value);
                              console.log(`تغيير الدائن للسطر رقم ${index} إلى القيمة ${newValue}`);
                              
                              // إذا كان المستخدم أضاف قيمة دائن، نجعل قيمة المدين فارغة للسطر نفسه
                              if (newValue !== '' && newValue !== 0) {
                                // استخدام معلمة additionalUpdate لتحديث المدين إلى فارغ في نفس العملية
                                handleLineChange(index, 'credit', newValue, { field: 'debit', value: '' });
                              } else {
                                // إذا كانت القيمة فارغة، فقط تحديث الدائن
                                handleLineChange(index, 'credit', newValue);
                              }
                            }}
                            size="small"
                            fullWidth
                            error={!!lineErrors[line.id]?.credit}
                            helperText={lineErrors[line.id]?.credit}
                            InputProps={{
                              inputProps: { min: 0 },
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Typography variant="caption" color="text.secondary">
                                    ريال
                                  </Typography>
                                </InputAdornment>
                              )
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="error"
                            onClick={() => removeLine(line.id, index)}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* صف المجاميع */}
                    <TableRow
                      key="totals-row"
                      sx={{
                        backgroundColor: alpha(theme.palette.background.default, 0.5),
                        fontWeight: 'bold'
                      }}
                    >
                      <TableCell colSpan={3} align="right" sx={{ fontWeight: 600 }}>
                        المجموع
                      </TableCell>
                      <TableCell sx={{
                        fontWeight: 700,
                        color: !isBalanced() ? 'error.main' : 'inherit',
                        borderBottom: !isBalanced() ? `2px solid ${theme.palette.error.main}` : undefined
                      }}>
                        {totals.debit.toLocaleString()} ريال
                      </TableCell>
                      <TableCell sx={{
                        fontWeight: 700,
                        color: !isBalanced() ? 'error.main' : 'inherit',
                        borderBottom: !isBalanced() ? `2px solid ${theme.palette.error.main}` : undefined
                      }}>
                        {totals.credit.toLocaleString()} ريال
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              
              {errors.balance && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errors.balance}
                </Alert>
              )}
              
              <Box sx={{ mt: 2, textAlign: 'left' }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addLine}
                  sx={{
                    borderColor: theme.palette.primary.main,
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                  }}
                >
                  إضافة سطر
                </Button>
              </Box>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            
            {/* رسالة النجاح */}
            {success && (
              <Alert
                severity="success"
                icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                sx={{ mb: 3 }}
              >
                تم {isEditMode ? 'تعديل' : 'إضافة'} القيد المحاسبي بنجاح
              </Alert>
            )}
            
            {/* رسالة الخطأ */}
            {apiError && (
              <Alert
                severity="error"
                icon={<ErrorOutlineIcon fontSize="inherit" />}
                sx={{ mb: 3 }}
              >
                {apiError}
              </Alert>
            )}
            
            {/* أزرار الإجراءات */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/finance/ledger')}
                disabled={saveLoading}
              >
                إلغاء
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                startIcon={isEditMode ? <SaveIcon /> : <AddIcon />}
                disabled={saveLoading || success || !isBalanced()}
                sx={{
                  bgcolor: '#30c3b0',
                  '&:hover': { bgcolor: '#25a899' },
                  fontWeight: 600,
                  px: 3,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(48, 195, 176, 0.2)',
                }}
              >
                {saveLoading ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : (
                  isEditMode ? 'حفظ التغييرات' : 'إضافة القيد'
                )}
              </Button>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default JournalEntryForm;